import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import { FileText, Copy, Pencil, AudioLines, ReceiptText } from "lucide-react";
import { getSignedUrl } from "@/api/imperative/uploads"
import { fetchAuthSessionWithLogout } from '@/features/auth/lib/authSession';
import StatusDropdown from '@/shared/components/StatusDropdown';
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import { downloadFilesAsZip } from "@/shared/lib/appHelpers"
import ActionButton from '@/shared/components/ActionButton'
import { INVOICE_APP_URL } from '@/shared/config/urls'

function ReportActions({ 
  report, 
  copyText, 
  allowedStatus, 
  reportStatus, 
  fileUpdating, 
  handleStatusUpdate, 
  handleCopyClicked,
  setEditClicked
}) {
  const { t } = useTranslation();
  const { toolOutputs, nonAudioTool, generation_name } = useCurrentTool();
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const getDocxDownloadUrl = async (report) => {
    const id = report.id;
    const accessToken = await fetchAuthSessionWithLogout();
    if (!accessToken) return null;
    const response = await fetch(
      `${import.meta.env.VITE_AWS_API_ENDPOINT}/md-to-docx-pandoc/${id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken.toString()}`,
        },
        body: JSON.stringify({}),
      }
    );
    if (!response.ok) return null;
    const docxDownloadLink = await response.json();
    return docxDownloadLink.downloadUrl;
  };

  const handleInvoice = async () => {
    try {
      setLoadingInvoice(true);
      const downloadUrl = await getDocxDownloadUrl(report);
      if (!downloadUrl) {
        toast.error("Error while preparing invoice. Please try again");
        setLoadingInvoice(false);
        return;
      }
      window.open(`${INVOICE_APP_URL}?file_url=${encodeURIComponent(downloadUrl)}`, '_blank');
    } catch (err) {
      console.log("Failed to prepare invoice, Full Error - ", err);
      toast.error("Error while preparing invoice. Please try again");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const downloadAudioFile = async () => {
    try {
      setDownloadingFile(true);
      const signedUrls = await getSignedUrl({
        name: report?.id,
        type: nonAudioTool ? "download_folder" : "download",
      });
      // Files in folder download
      if (nonAudioTool) {
        const zipFileName = `${generation_name}_${report?.row_number}`;
        await downloadFilesAsZip(signedUrls, zipFileName);
      } else {
        // Single file download
        window.open(signedUrls, '_self');
      }
    } catch (error) {
      console.log("Downloading error ", error);
      toast.error("Error while downloading the file. Please try again");
    } finally {
      setDownloadingFile(false);
    }
  };

  const convertMdtoDocx = async (report) => {
    try {
      if (downloadingReport) return;
      setDownloadingReport(true);
      const downloadUrl = await getDocxDownloadUrl(report);
      if (!downloadUrl) {
        setDownloadingReport(false);
        toast.error("Error, could not fetch converted docx");
        return;
      }
      const link = document.createElement("a");
      link.download = `${generation_name} - ${report.row_number}.docx`;
      link.href = downloadUrl;
      setDownloadingReport(false);
      link.click();
    } catch (err) {
      console.log("Failed to convert report to docx, Full Error - ", err);
      setDownloadingReport(false);
    }
  };

  const renderActionButton = ({ onClick, isLoading, icon: Icon, tooltip }) => {
    return (
      <ActionButton
        key={tooltip}
        icon={Icon}
        tooltip={tooltip}
        onClick={onClick}
        isLoading={isLoading}
        className="border cursor-pointer px-2.5 rounded-[10px] h-10 w-[40px] hover:border-[#CED0D5] border-[#E2E4E9] flex justify-center items-center"
      />
    );
  };

  const getToolOutputs = () => {
    const outputOrder = ["invoice", "audio_download", "pdf_download", "docx_download"];
    const sortedOutputs = [...(toolOutputs || [])].sort((a, b) => {
      const indexA = outputOrder.indexOf(a.type);
      const indexB = outputOrder.indexOf(b.type);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return sortedOutputs.map((output) => {
      if (output.type === "audio_download" || output.type === 'pdf_download') {
        return renderActionButton({
          onClick: downloadAudioFile,
          isLoading: downloadingFile,
          icon: AudioLines,
          tooltip: nonAudioTool ? t("actions.downloadPDF") : t("actions.downloadAudio")
        });
      }
      if (output.type === "docx_download") {
        return renderActionButton({
          onClick: () => convertMdtoDocx(report),
          isLoading: downloadingReport,
          icon: FileText,
          tooltip: t("actions.downloadDocument")
        });
      }
      if (output.type === "invoice") {
        return renderActionButton({
          onClick: handleInvoice,
          isLoading: loadingInvoice,
          icon: ReceiptText,
          tooltip: t("actions.generateInvoice")
        });
      }
      return null;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full grid grid-rows-1 sm:grid-cols-2 gap-3">
        <div className="flex gap-3 items-center">
          <CopyToClipboard
            disabled={!report.report}
            text={!!report.report ? copyText : "N/A"}
            onCopy={handleCopyClicked}
          >
            {renderActionButton({
              icon: Copy,
              tooltip: t("actions.copy")
            })}
          </CopyToClipboard>
          <div className="block w-full">
            {!!report.status && (
              <StatusDropdown
                isDrawer
                tabs={allowedStatus}
                mobileView={true}
                disable={fileUpdating}
                selected={reportStatus ? reportStatus?.toLowerCase() : report.status?.toLowerCase()}
                reports={false}
                handler={handleStatusUpdate}
              />
            )}
          </div>
        </div>
        <div className="flex gap-3 sm:place-self-end">
          {getToolOutputs()}
          {renderActionButton({
            onClick: () => setEditClicked(true),
            icon: Pencil,
            tooltip: t("actions.edit")
          })}
        </div>
      </div>
    </div>
  );
}

export default ReportActions;
